import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Flame, Heart, BookOpen, Award, Target, Star, Gift, CheckCircle2, Play, Zap, Headphones } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';
import LevelProgress from './LevelProgress';
import AcademyNotifySettings from './AcademyNotifySettings';
import Leaderboard from './Leaderboard';
import StreakCalendar from './StreakCalendar';
import DailyChallenge from './DailyChallenge';
import { getT } from './i18n';

export default function Dashboard({ token, user, lang = 'fr', onResume, onPlayQuick, onOpenScenarios, onOpenListening, onOpenReadings, onOpenReview, onOpenAudiobooks, onOpenCertificates }) {
  const t = getT(lang);
  const [data, setData]         = useState(null);
  const [missions, setMissions] = useState([]);
  const [badges, setBadges]     = useState({ earned: [], catalog: [] });
  const [continuity, setCont]   = useState(null);

  const reload = useCallback(async () => {
    try {
      const [d, m, b, c] = await Promise.all([
        apiCall('/dashboard', 'GET', null, token),
        apiCall('/missions/today', 'GET', null, token),
        apiCall('/badges/me', 'GET', null, token),
        apiCall('/continuity', 'GET', null, token).catch(() => ({})),
      ]);
      setData(d); setMissions(m.missions || []); setBadges(b);
      setCont(c && c.lesson_id ? c : null);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const claim = async (mid) => {
    try {
      const r = await apiCall('/missions/claim', 'POST', { mission_id: mid }, token);
      toast.success(`+${r.xp_gained} XP ${lang === 'fr' ? 'réclamés' : 'claimed'} !`);
      reload();
    } catch (e) { toast.error(e.message); }
  };

  if (!data) return <div className="max-w-5xl mx-auto px-4 py-12 text-white/60">{t('dash_loading')}</div>;
  const u = data.user, s = data.stats;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="academy-dashboard">

      {/* Salutation + niveau utilisateur */}
      <div className="bg-gradient-to-br from-emerald-700/40 via-slate-900 to-yellow-900/20 rounded-3xl p-5 border border-emerald-500/20">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <div className="text-xs text-emerald-300 uppercase tracking-wider mb-0.5">{t('dash_eyebrow')}</div>
            <h2 className="text-2xl font-bold">{t('dash_hi')} {u.name} 👋</h2>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <BigStat icon={Flame}  value={`${u.streak_days || 0}${lang === 'fr' ? 'j' : 'd'}`}  label={t('dash_streak')} color="text-orange-400" />
            <BigStat icon={Heart}  value={u.lives ?? 5}              label={t('dash_lives')}   color="text-red-400" />
            <BigStat icon={Award}  value={(badges.earned||[]).length} label={t('dash_badges')} color="text-pink-300" />
          </div>
        </div>
        <LevelProgress xp={u.xp || 0} completedLessons={s.completed_lessons || 0} lang={lang} />
        {/* Global progression bar */}
        <div className="mt-1">
          <div className="flex items-center justify-between text-xs text-white/60 mb-1.5"><span>{t('dash_global_progress')}</span><span>{s.global_percent}%</span></div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-yellow-400" style={{ width: `${s.global_percent}%` }} />
          </div>
          <div className="text-[11px] text-white/40 mt-1">{s.completed_lessons} / {s.total_lessons} {t('dash_lessons_done')}</div>
        </div>
      </div>

      {/* "Commencer maintenant" — prioritaire en haut */}
      <div className="bg-gradient-to-br from-yellow-500/15 to-emerald-500/15 border-2 border-yellow-400/40 rounded-2xl p-5" data-testid="dashboard-cta-block">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center"><Zap size={16} className="text-slate-950" /></div>
          <h3 className="font-bold text-lg">{t('dash_start_now')} 👇</h3>
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          {continuity ? (
            <button onClick={() => onResume(continuity)} data-testid="dashboard-resume"
              className="text-left bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-xl p-3 font-bold transition flex items-center gap-2">
              <Play size={18} fill="currentColor" />
              <div>
                <div className="text-xs uppercase tracking-wider opacity-70">{t('dash_resume')}</div>
                <div className="text-sm">{t('dash_step')} {continuity.step}</div>
              </div>
            </button>
          ) : (
            <button onClick={onPlayQuick} className="text-left bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-xl p-3 font-bold transition flex items-center gap-2" data-testid="dashboard-start-first">
              <Play size={18} fill="currentColor" />
              <div>
                <div className="text-xs uppercase tracking-wider opacity-70">{lang === 'fr' ? 'Démarrer' : 'Start'}</div>
                <div className="text-sm">{t('dash_start_first')}</div>
              </div>
            </button>
          )}
          <button onClick={onPlayQuick} data-testid="dashboard-quick"
            className="text-left bg-white/10 hover:bg-white/20 rounded-xl p-3 font-bold transition flex items-center gap-2">
            <span className="text-2xl">⏱️</span>
            <div>
              <div className="text-xs uppercase tracking-wider text-white/60">{lang === 'fr' ? "J'ai 5 min" : 'I have 5 min'}</div>
              <div className="text-sm">{t('dash_quick_play')}</div>
            </div>
          </button>
          <button onClick={onOpenListening} data-testid="dashboard-listen"
            className="text-left bg-white/10 hover:bg-white/20 rounded-xl p-3 font-bold transition flex items-center gap-2">
            <Headphones size={20} className="text-emerald-300" />
            <div>
              <div className="text-xs uppercase tracking-wider text-white/60">{lang === 'fr' ? 'Mode' : 'Mode'}</div>
              <div className="text-sm">{lang === 'fr' ? 'Écoute libre' : 'Free listening'}</div>
            </div>
          </button>
        </div>
      </div>

      {/* DAILY CHALLENGE — défi unique du jour avec gros XP */}
      <DailyChallenge token={token} lang={lang} onXpGained={() => reload()} />

      {/* Quick actions secondaires */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <ActionCard onClick={onOpenScenarios}    icon="🎬" title={t('dash_scenarios')}    desc={t('dash_scenarios_desc')} test="scenarios-card" />
        <ActionCard onClick={onOpenReadings}     icon="📖" title={lang === 'fr' ? 'Bibliothèque' : 'Library'} desc={lang === 'fr' ? 'Lectures audio libres' : 'Free audio readings'} test="readings-card" />
        <ActionCard onClick={onOpenReview}       icon="🔁" title={t('dash_review')}       desc={t('dash_review_desc')} test="review-card" />
        <ActionCard onClick={onOpenAudiobooks}   icon="📖" title={lang === 'fr' ? 'Audiolivres' : 'Audiobooks'} desc={lang === 'fr' ? 'Écoute + résumé' : 'Listen + summary'} test="audiobooks-card" />
        <ActionCard onClick={onOpenCertificates} icon="🏆" title={t('dash_certificates')} desc={t('dash_certificates_desc')} test="certificates-card" />
        <ActionCard onClick={onOpenListening}    icon="🎧" title={lang === 'fr' ? 'Playlist' : 'Playlist'} desc={t('dash_listening_desc')} test="listening-card" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Missions */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><Target size={18} className="text-emerald-300" /><h3 className="font-bold">{t('dash_daily_missions')}</h3></div>
          <div className="space-y-2">
            {missions.map(m => (
              <div key={m.id} data-testid={`mission-${m.id}`} className="bg-slate-900/60 border border-white/10 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-bold flex items-center gap-2">{m.completed && <CheckCircle2 size={14} className="text-emerald-400" />}{m.label}</div>
                  <div className="text-xs text-yellow-300 font-bold">+{m.reward_xp} XP</div>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-emerald-400 transition-all" style={{ width: `${Math.min(100, (m.progress / m.target) * 100)}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white/50">{m.progress}/{m.target}</span>
                  {m.completed && !m.claimed && (
                    <button onClick={() => claim(m.id)} data-testid={`claim-${m.id}`} className="bg-yellow-500 text-slate-950 font-bold px-3 py-1 rounded-full text-[11px] flex items-center gap-1"><Gift size={11} />{t('dash_claim')}</button>
                  )}
                  {m.claimed && <span className="text-emerald-400 font-bold">✓ {t('dash_claimed')}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Badges */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Star size={18} className="text-yellow-400" /><h3 className="font-bold">Badges ({badges.earned.length}/{badges.catalog.length})</h3></div>
            <button onClick={onOpenCertificates} className="text-xs text-emerald-300 hover:underline" data-testid="open-certificates">Voir certificats →</button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {badges.catalog.map(b => {
              const got = badges.earned.includes(b.id);
              return (
                <div key={b.id} title={b.desc} data-testid={`badge-${b.id}`}
                  className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center text-center p-1 ${got ? 'border-yellow-400 bg-yellow-500/10' : 'border-white/10 bg-white/5 opacity-40 grayscale'}`}>
                  <div className="text-2xl">{b.icon}</div>
                  <div className="text-[9px] text-white/70 mt-1 leading-tight">{b.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Daily reminders */}
        <AcademyNotifySettings token={token} user={user} />

        {/* NEW: Streak calendar heatmap */}
        <StreakCalendar token={token} lang={lang} />

        {/* NEW: Weekly leaderboard */}
        <Leaderboard token={token} lang={lang} />
      </div>
    </motion.div>
  );
}

function BigStat({ icon: Icon, value, label, color }) {
  return (
    <div className="bg-black/20 backdrop-blur rounded-xl p-2 border border-white/10 text-center min-w-0">
      <Icon size={14} className={`mx-auto ${color}`} />
      <div className="font-bold text-sm">{value}</div>
      <div className="text-[8px] text-white/50 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function ActionCard({ onClick, icon, title, desc, test }) {
  return (
    <button onClick={onClick} data-testid={test} className="text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/40 rounded-2xl p-3 transition group">
      <div className="text-2xl mb-1.5 group-hover:scale-110 transition-transform">{icon}</div>
      <div className="font-bold text-sm">{title}</div>
      <div className="text-[10px] text-white/60">{desc}</div>
    </button>
  );
}
